import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MessageService } from '@services/helpers/message-service/message.service';
import { SettingsProvider } from '@services/settings/settings';
import { QuoteUpdateService } from '@services/quote/quote-update.service';
import { TimeServiceProvider } from '@services/helpers/time-service/time.service';
import { ShootProofDataProvider } from '@services/integrations/shootProof/shootProof.data.service';
import { InvoicesDataProvider } from '../../../../../../../shared/services/invoice/invoices-data-service/invoices.data.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { PickTimeDataProvider } from '@services/integrations/pickTime/pickTime.data.service';
import {
	CreatePicTimeGallery,
	GetPicTimeTemplates,
	GetRedirectPicTimeUrl,
	JobMetadata,
	PicTimeClientInfo,
	ServerDataResponse,
	ShootProofGalleriesBrand
} from '@models/models';
import { finalize, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';
import { IntegrationsServiceProvider } from '@services/integrations/integrations.service';
import { SNModalService } from '@web-services/modal/snModal.service';
import { ConfirmGeneralModalComponent } from '@web-components/common/confirm-general-modal/confirm-general-modal.component';
import { PlatformLocation } from '@angular/common';
import { AddGalleryModal } from '@web-components/integrations/connect-galleries/modals/add-gallery-modal/add-gallery.modal';

@Component({
	selector: 'create-new-gallery-modal',
	template: require('./create-new-gallery.modal.html'),
	styles: [require('./create-new-gallery.modal.scss')]
})
export class CreateNewGalleryModal implements OnInit, OnDestroy {
	@Input('shootProofBrands') shootProofBrands: Array<ShootProofGalleriesBrand>;
	@Input('jobId') jobId: number;
	@Input('jobMetaData') jobMetaData: JobMetadata;
	@Input('picTimeTemplates') picTimeTemplates: GetPicTimeTemplates[];
	@Input('clientList') clientList: Array<PicTimeClientInfo>;

	name: string;
	galleryName: string;
	projectUrl: string;
	galleryMainShootDate: string;
	hardcodedString: string;
	changeableString: string;
	isBrandPristine = false;
	_jobName: string;
	@Input('jobName')
	set jobName(value) {
		this.name = value;
		this._jobName = value;
	}

	get jobName() {
		return this._jobName;
	}

	eventDate;
	dateTaken;
	_mainShootDate;
	@Input('mainShootDate')
	set mainShootDate(value) {
		this.eventDate =
			value === null ? null : this.quoteUpdateService.setQuoteIssueDate(value);
		this._mainShootDate =
			value === null ? null : this.quoteUpdateService.setQuoteIssueDate(value);
	}
	get mainShootDate() {
		return this.eventDate;
	}

	datePlaceHolder = '';
	staticBrandId = -1;
	selectedBrandId = -1;
	selectedPresetId = -1;
	submitDisable = false;
	spBrandThemeId = null;
	selectedTemplateId = -1;
	picTimeBrands = [];

	readonly DEFAULT_GALLERY_PRESET = [{ id: -1, name: 'Choose Preset' }];
	readonly LOADING_GALLERY_PRESET = [{ id: -1, name: 'Loading...' }];

	disabledGalleryPresetSelect = false;
	galleryPresets = this.DEFAULT_GALLERY_PRESET;

	private unsubscribe: Subject<void> = new Subject();

	constructor(
		private shootProofDataService: ShootProofDataProvider,
		private pickTimeDataProvider: PickTimeDataProvider,
		private messageService: MessageService,
		private settingsService: SettingsProvider,
		private quoteUpdateService: QuoteUpdateService,
		private timeService: TimeServiceProvider,
		private invoiceDataService: InvoicesDataProvider,
		private activeModal: NgbActiveModal,
		private integrationService: IntegrationsServiceProvider,
		private modalService: SNModalService,
		private location: PlatformLocation
	) {
		location.onPopState(() => this.activeModal.close());
	}

	ngOnInit(): void {
		if (!this.shootProofBrands) {
			this.initPicTimeMetadata();
		}
		this.getTimeSettings();
	}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	dismiss(): void {
		this.disabledGalleryPresetSelect = false;
		this.name = this.jobName;
		this.submitDisable = false;
		this.galleryPresets = this.DEFAULT_GALLERY_PRESET;
		this.shootProofBrands = [];
		this.eventDate = this._mainShootDate;
		this.staticBrandId = -1;
		this.selectedBrandId = -1;
		this.selectedPresetId = -1;
		this.selectedTemplateId = -1;
		this.picTimeTemplates = [];
		this.isBrandPristine = false;
		this.activeModal.close();
	}

	initPicTimeMetadata(): void {
		this.galleryName = this.jobMetaData.name;
		this.galleryMainShootDate = this.jobMetaData.mainShootDate;
		this.picTimeBrands = this.jobMetaData.brandDomainMetadata;
		this.dateTaken = this.jobMetaData.mainShootDate
			? this.quoteUpdateService.setQuoteIssueDate(
					this.jobMetaData.mainShootDate
			  )
			: null;

		if (this.picTimeBrands.length === 1) {
			this.staticBrandId = this.picTimeBrands[0].brandId;
			this.hardcodedString = `https://${this.picTimeBrands[0].domain}/-`;
			this.changeableString = this.jobMetaData.name.toLowerCase();
		}
	}

	selectPicTimeBrand(brandId) {
		if (brandId != -1) {
			this.hardcodedString = `https://${
				this.picTimeBrands.find(value => value.brandId === brandId).domain
			}/-`;
			if (!this.isBrandPristine) {
				this.changeableString = this.jobMetaData.name.toLowerCase();
				this.isBrandPristine = true;
			}
		} else {
			this.selectedBrandId = -1;
			this.hardcodedString = '';
			this.changeableString = '';
		}
	}

	selectBrand(brandId): void {
		if (brandId != -1) {
			this.selectedBrandId = brandId;
			this.getPresets(brandId);
			this.getSelectedBrandThemeId();
		} else {
			this.selectedBrandId = -1;
			this.selectedPresetId = -1;
			this.galleryPresets = this.DEFAULT_GALLERY_PRESET;
		}
	}

	selectPreset(presetId): void {
		this.selectedPresetId = presetId;
	}

	getSelectedBrandThemeId(): void {
		this.shootProofBrands.forEach(item => {
			if (this.selectedBrandId == item.spBrandId) {
				this.spBrandThemeId = item.spHomeBrandThemeId;
			}
		});
	}

	getPresets(BrandId: number): void {
		this.disabledGalleryPresetSelect = true;
		this.galleryPresets = this.LOADING_GALLERY_PRESET;

		this.shootProofDataService
			.getGalleryPresetsV1(BrandId)
			.finally(() => (this.disabledGalleryPresetSelect = false))
			.subscribe(
				(data: any) => {
					this.galleryPresets = this.DEFAULT_GALLERY_PRESET.concat(data.data);
				},
				error => {
					this.galleryPresets = this.DEFAULT_GALLERY_PRESET;
					this.messageService.showDefaultErrorMessage(error);
				}
			);
	}

	private getTimeSettings(): void {
		this.settingsService.timeFormatSettings.subscribe((data): void => {
			switch (data.dateFormat) {
				case 'DD_MM_YYYY':
					this.datePlaceHolder = 'dd/mm/yyyy';
					break;
				case 'MM_DD_YYYY':
					this.datePlaceHolder = 'mm/dd/yyyy';
					break;
				default:
					this.datePlaceHolder = '';
					break;
			}
		});
	}

	private validationCreateNewGallery(): boolean {
		let validation = true;

		if (!this.name || this.name.trim() === '') {
			validation = false;
			this.messageService.showErrorFromBackend(
				'Oh Shoot!',
				'Please enter a name for your new gallery.'
			);
		} else if (this.selectedBrandId === -1) {
			validation = false;
			this.messageService.showErrorFromBackend(
				'Oh Shoot!',
				'Please select a brand for your new gallery.'
			);
		} else if (!this.eventDate || this.eventDate === null) {
			validation = false;
			this.messageService.showErrorFromBackend(
				'Oh Shoot!',
				'Please enter a shoot date for your new gallery.'
			);
		}

		return validation;
	}

	private validatePicTimeGalleryCreation(): boolean {
		if (this.staticBrandId === -1 && this.selectedBrandId === -1) {
			this.messageService.showToaster('e_7', 'picTime');
			return false;
		} else if (this.galleryName.length === 0) {
			this.messageService.showToaster('e_0', 'picTime');
			return false;
		} else if (this.changeableString.length === 0) {
			this.messageService.showToaster('e_1', 'picTime');
			return false;
		} else if (!this.validateProjectUrl()) {
			this.messageService.showToaster('e_3', 'picTime');
			return false;
		} else if (!this.dateTaken) {
			this.messageService.showToaster('e_8', 'picTime');
			return false;
		}

		return true;
	}

	private validateProjectUrl(): boolean {
		const regex = /[a-z0-9\\_\\]+$/;
		if (!this.changeableString.match(regex)) {
			return false;
		}

		return true;
	}

	onSubmit(): void {
		if (this.shootProofBrands) {
			if (!this.validationCreateNewGallery()) {
				return;
			}
			const settings = {
				jobId: this.jobId,
				spBrandId: +this.selectedBrandId,
				eventDate: this.timeService.getDateInFormat(
					this.eventDate.jsdate,
					'date'
				),
				name: this.name,
				galleryPresetId:
					this.selectedPresetId !== -1 ? +this.selectedPresetId : null,
				spBrandThemeId: this.spBrandThemeId
			};
			this.submitDisable = true;
			this.shootProofDataService
				.createShootProofNewGalleryFromSNV1(settings)
				.pipe(finalize((): boolean => (this.submitDisable = false)))
				.subscribe(
					(): void => this.successCreateNewShootProofGallery(),
					(error): void => this.messageService.handleError(error)
				);
		} else {
			if (!this.validatePicTimeGalleryCreation()) {
				return;
			}

			this.projectUrl = this.hardcodedString + this.changeableString;
			const createPicTimeGallery: CreatePicTimeGallery = {
				galleryName: this.galleryName,
				virtualPath: this.changeableString,
				templateId:
					this.selectedTemplateId !== -1 ? this.selectedTemplateId : null,
				brandId:
					this.staticBrandId !== -1 ? this.staticBrandId : this.selectedBrandId,
				dateTaken: this.dateTaken
					? this.timeService.getDateInFormat(this.dateTaken.jsdate, 'date')
					: null
			};

			this.confirmApplyingClientData()
				.then((): void => {
					createPicTimeGallery.clientList = this.clientList;
					this.savePicTimeGallery(createPicTimeGallery);
				})
				.catch((): void => this.savePicTimeGallery(createPicTimeGallery));
		}
	}

	private savePicTimeGallery(galleryPayload: CreatePicTimeGallery): void {
		this.submitDisable = true;
		this.pickTimeDataProvider
			.createPicTimeGallery(this.jobId, galleryPayload)
			.pipe(
				takeUntil(this.unsubscribe),
				finalize((): boolean => (this.submitDisable = false))
			)
			.subscribe(
				(data: ServerDataResponse<GetRedirectPicTimeUrl>): void =>
					this.successCreateNewPicTimeGallery(data.data),
				(error): void => this.messageService.handleError(error)
			);
	}

	private successCreateNewShootProofGallery(): void {
		this.messageService.showToaster('s_1', 'shootProof');
		this.invoiceDataService.emitInvoiceUpdateEvent('UPDATE_SP_INVOICES');
		this.dismiss();
		this.integrationService.emitGalleriesUpdateEvent('UPDATE_SN_GALLERIES');
	}

	private successCreateNewPicTimeGallery(data: GetRedirectPicTimeUrl): void {
		this.messageService.showToaster('s_3', 'picTime');
		this.invoiceDataService.emitInvoiceUpdateEvent('UPDATE_PT_INVOICES');
		this.dismiss();
		this.integrationService.emitGalleriesUpdateEvent('UPDATE_SN_GALLERIES');
		window.open(data.configureGalleryUrl, '_blank');
	}

	private confirmApplyingClientData(): Promise<void> {
		return new Promise((resolve, reject): void => {
			const modal = this.modalService.openModal(ConfirmGeneralModalComponent, {
				windowClass: 'ng-bootstrap-modal-small modal-visible modal-narrow'
			});
			modal.componentInstance.text = this.integrationService.createTextForApplyConfirm(
				this.clientList
			);
			modal.componentInstance.htmlText = true;
			modal.componentInstance.buttons = [
				{
					text: 'Yes',
					size: 'lg',
					color: 'success',
					action: 'APPLY_AND_SAVE'
				},
				{
					text: 'No',
					size: 'lg',
					color: 'default',
					action: 'DISCARD_AND_SAVE'
				},
				{
					text: 'Cancel',
					size: 'lg',
					color: 'default',
					action: 'DISCARD'
				}
			];

			modal.result.then((action: string): void => {
				if (action === 'APPLY_AND_SAVE') {
					resolve();
				}

				if (action === 'DISCARD_AND_SAVE') {
					reject();
				}

				if (action === 'DISCARD') {
					this.modalService.closeActiveModal();
				}
			});
		});
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=os2z8rc99lf!./web/modern/components/integrations/connect-galleries/modals/create-new-gallery-modal/create-new-gallery.modal.ts